<h2 mat-dialog-title class="mfa-title">
  <i class="fa fa-shield" aria-hidden="true"></i>
  Multi-Factor Authentication Settings
</h2>

<mat-dialog-content class="mfa-settings" data-test="mfa-settings">
  @if (step === 'method') {
    <form id="mfa-method-form" class="mfa-step" [formGroup]="methodForm" (ngSubmit)="submitMethod()" data-test="mfa-method-step">
      <mat-form-field appearance="outline" floatLabel="always" class="mfa-select">
        <mat-label>Delivery Method</mat-label>
        <mat-select id="mfa-delivery-method" formControlName="method" data-test="mfa-delivery-method">
          @for (option of methods; track option.value) {
            <mat-option [value]="option.value">{{ option.label }}</mat-option>
          }
        </mat-select>
        @if (currentMethodLabel) {
          <mat-hint align="start">Currently active: {{ currentMethodLabel }}</mat-hint>
        }
        @if (methodForm.get('method').invalid && methodForm.get('method').touched) {
          <mat-error>Please select a delivery method.</mat-error>
        }
      </mat-form-field>

      <p>
        Multi-Factor Authentication (MFA) adds an extra layer of security to your TradeCafe account by requiring not only your password but also a second form of verification, such as a code sent to your email or mobile device.
      </p>
      <p>
        This ensures that even if your password is compromised, unauthorized access is prevented. Administrators can enforce MFA across the entire account, while individual users can choose their preferred method of verification. With MFA enabled, your account is better protected against fraudulent activities, ensuring your data and transactions remain secure.
      </p>
      <p>We highly recommend <strong>SMS</strong> or <strong>WhatsApp</strong> delivery methods.</p>

      @if (submitError) {
        <div class="alert alert-danger" role="alert">{{ submitError }}</div>
      }
    </form>
  } @else {
    <form id="mfa-verify-form" class="mfa-step" [formGroup]="verifyForm" (ngSubmit)="verifyCode()" data-test="mfa-verify-step">
      <p class="verify-instructions">
        Input the code we sent via
        <span class="channel">{{ channelDisplay || 'contact information' }}</span>
        @if (destination) {
          to: <strong>{{ destination }}</strong>
        }
      </p>

      <mat-form-field appearance="outline">
        <mat-label>MFA Code</mat-label>
        <input matInput maxlength="6" formControlName="otp" inputmode="numeric" autocomplete="one-time-code"
          placeholder="Enter 6-digit code" aria-required="true" data-test="mfa-otp-input">
        @if (verifyForm.get('otp').hasError('required') && verifyForm.get('otp').touched) {
          <mat-error>MFA code is required.</mat-error>
        }
        @if (verifyForm.get('otp').hasError('pattern') && verifyForm.get('otp').touched) {
          <mat-error>Please enter a 6 digit code.</mat-error>
        }
      </mat-form-field>

      @if (verifyError) {
        <div class="alert alert-danger" role="alert">{{ verifyError }}</div>
      }
    </form>
  }
</mat-dialog-content>

@if (step === 'method') {
  <mat-dialog-actions align="end">
    <button mat-button type="button" class="link" (click)="cancel()" data-test="mfa-cancel">Cancel</button>
    <button mat-raised-button color="primary" type="submit" [attr.form]="'mfa-method-form'" data-test="mfa-save"
      [disabled]="isInitiating || methodForm.invalid || isCurrentMethodSelected">
      <i class="fa" aria-hidden="true"
        [class.fa-lock]="!isInitiating"
        [class.fa-spinner]="isInitiating"
        [class.fa-spin]="isInitiating"></i>
      Save
    </button>
  </mat-dialog-actions>
} @else {
  <mat-dialog-actions align="start">
    <button mat-stroked-button type="button" class="resend-button mat-action" data-test="mfa-resend"
      (click)="resendCode()"
      [disabled]="resendDisabledSeconds > 0 || isResending">
      <i class="fa" aria-hidden="true"
        [class.fa-paper-plane]="!(isResending || resendDisabledSeconds > 0)"
        [class.fa-spinner]="isResending"
        [class.fa-spin]="isResending"></i>
      Resend Code
      @if (resendDisabledSeconds > 0) {
        <span>({{ resendDisabledSeconds }}s)</span>
      }
    </button>
    <span class="flex-spacer"></span>
    <button mat-button type="button" class="link" (click)="goBackToMethod()" data-test="mfa-back">
      Back
    </button>
    <button mat-raised-button color="primary" type="submit" [attr.form]="'mfa-verify-form'" data-test="mfa-verify"
      [disabled]="isVerifying || verifyForm.invalid">
      <span>Verify</span>
      <i class="fa" aria-hidden="true"
        [class.fa-arrow-right]="!isVerifying"
        [class.fa-spinner]="isVerifying"
        [class.fa-spin]="isVerifying"></i>
    </button>
  </mat-dialog-actions>
}
